import { useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  Input,
  InputGroup,
  MultipleChoice,
  Select,
  ToggleSwitch,
  ToggleSwitchInput,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import styles from './RenewalConfigModal.module.scss';

export const RenewalConfigModal = ({
  companyUsers,
  brands,
  renewalConfig,
  companyChecks,
  visible,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const [checkType, setCheckType] = useState();
  const [companyCheck, setCompanyCheck] = useState();

  const { t } = useTranslation();

  useEffect(() => {
    if (!renewalConfig) {
      setCompanyCheck(null);
      setCheckType(null);
      return;
    }
    setCheckType(renewalConfig.type);
  }, [renewalConfig]);

  useEffect(() => {
    if (!checkType) {
      setCompanyCheck(null);
      return;
    }
    setCompanyCheck(companyChecks.find(cc => cc.type === checkType));
  }, [checkType]);

  const handleSubmit = data => {
    setCheckType(false);
    onSubmit(data);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={
        renewalConfig
          ? t('renewals.tabs.configuration.edit_renewal')
          : t('renewals.tabs.configuration.add_renewal')
      }
      description={
        companyCheck
          ? companyCheck.check_type?.title
          : 'Select a check type to create a new renewal configuration.'
      }
      className="u-margin-y--large"
      small={true}>
      {checkType && companyCheck ? (
        <RenewalConfigForm
          renewalConfig={renewalConfig}
          companyCheck={companyCheck}
          companyUsers={companyUsers}
          brands={brands}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      ) : (
        <TypeSelector companyChecks={companyChecks} onSelect={setCheckType} onClose={onClose} />
      )}
    </AnimatedModal>
  );
};

const TypeSelector = ({ companyChecks, onSelect = () => {} }) => {
  const [selectedType, setSelectedType] = useState(null);

  return (
    <div className="u-margin-top">
      {companyChecks.map(companyCheck => (
        <div
          key={companyCheck.type}
          className="card card-with-border d-flex align-items-center justify-content-between u-padding u-margin-top u-pointer"
          onClick={() => setSelectedType(companyCheck.type)}>
          <div className="d-flex align-items-center">
            <IconComponent
              icon={getCheckTypeIcon(companyCheck.type)}
              className="u-margin-right--small"
            />
            <p className="title-4">{companyCheck.check_type.title}</p>
          </div>
          <span
            className={cn(styles.radioButton, {
              [styles.radioButtonSelected]: companyCheck.type === selectedType,
            })}></span>
        </div>
      ))}
      <div className="d-flex justify-content-end">
        <Button
          className="u-margin-top--large"
          type="primary"
          onClick={() => onSelect(selectedType)}>
          Continue
        </Button>
      </div>
    </div>
  );
};

const RenewalConfigForm = ({
  renewalConfig,
  companyCheck,
  companyUsers = [],
  brands = [],
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();

  const { register, watch, reset, control, setValue, handleSubmit } = useForm({
    defaultValues: {
      name: renewalConfig?.name || companyCheck?.check_type?.title,
      renewal_mode: renewalConfig?.renewal_mode || 'use_expiration',
      frequency: renewalConfig?.frequency || 365,
      days_before_expiration: renewalConfig?.days_before_expiration || 0,
    },
  });

  const [useCustomBrand, setUseCustomBrand] = useState(false);
  const [useCustomUser, setUseCustomUser] = useState(false);

  useEffect(() => {
    if (!renewalConfig) {
      return;
    }

    if (renewalConfig.brand_id) {
      setUseCustomBrand(true);
    }
    if (renewalConfig.user_id) {
      setUseCustomUser(true);
    }

    reset(renewalConfig);
  }, [renewalConfig]);

  const renewalModeOptions = useMemo(() => {
    let options = [];

    if (companyCheck.check_type.has_expiration_date) {
      options.push({
        name: t('renewals.tabs.configuration.use_expiration'),
        value: 'use_expiration',
        description: t('renewals.tabs.configuration.use_expiration_description'),
      });
    }

    options.push({
      name: t('renewals.tabs.configuration.use_fixed_cadence'),
      value: 'fixed',
      description: t('renewals.tabs.configuration.use_fixed_cadence_description'),
    });

    options.push({
      name: t('renewals.tabs.configuration.conditional_renewal'),
      value: 'conditional',
      description: t('renewals.tabs.configuration.conditional_renewal_description'),
    });

    return { options };
  }, [companyCheck]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input hidden {...register('type')} value={companyCheck.type} />
      <input hidden {...register('company_id')} value={companyCheck.company_id} />
      <InputGroup title={t('renewals.tabs.configuration.renewal_name')}>
        <Input name="name" register={register} />
      </InputGroup>
      <ToggleSwitchInput
        text={t('renewals.tabs.configuration.use_custom_brand')}
        subtext={t('renewals.tabs.configuration.use_custom_brand_description')}
        onChange={e => {
          setUseCustomBrand(e.target.checked);
          if (!e.target.checked) {
            setValue('brand_id', null);
          }
        }}
        value={useCustomBrand}
        className="u-width-100">
        {useCustomBrand && (
          <Select name="brand_id" register={register} value={watch('brand_id')} useDefault>
            {brands.map(brand => (
              <Select.Item key={brand.id} value={brand.id}>
                {brand.name}
              </Select.Item>
            ))}
          </Select>
        )}
      </ToggleSwitchInput>
      <ToggleSwitchInput
        text={t('renewals.tabs.configuration.use_custom_user')}
        subtext={t('renewals.tabs.configuration.use_custom_user_description')}
        onChange={e => {
          setUseCustomUser(e.target.checked);
          if (!e.target.checked) {
            setValue('user_id', null);
          }
        }}
        value={useCustomUser}
        className="u-width-100">
        {useCustomUser && (
          <Select name="user_id" register={register} value={watch('user_id')} useDefault>
            {companyUsers.map(user => (
              <Select.Item key={user.id} value={user.id}>
                {user.first_name} {user.last_name} • {user.email}
              </Select.Item>
            ))}
          </Select>
        )}
      </ToggleSwitchInput>

      <ToggleSwitch
        name="auto_renew"
        register={register}
        className={styles.compactToggleSwitch}
        checked={watch('auto_renew')}
        tooltip={t('renewals.tabs.configuration.auto_renewal_description')}
        label={t('renewals.tabs.configuration.auto_renewal')}
      />

      <InputGroup title={t('renewals.tabs.configuration.renewal_mode')}>
        <Controller
          name="renewal_mode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultipleChoice
              value={value}
              onChange={onChange}
              radioButtonStyle
              noLabel
              options={renewalModeOptions}
            />
          )}
        />
      </InputGroup>

      {watch('renewal_mode') !== 'use_expiration' ? (
        <div className="card card-with-border u-padding">
          {watch('renewal_mode') === 'conditional' && (
            <>
              <InputGroup title={t('renewals.tabs.configuration.input_source')}>
                <Select
                  name="condition_input_source"
                  register={register}
                  value={watch('condition_input_source')}
                  useDefault>
                  <Select.Item value="candidate_checks.details">
                    {t('renewals.tabs.configuration.input_source_options.launching_details')}
                  </Select.Item>
                  <Select.Item value="candidates_checks_values.values">
                    {t('renewals.tabs.configuration.input_source_options.candidate_details')}
                  </Select.Item>
                  <Select.Item value="candidate_checks.extra_data">
                    {t('renewals.tabs.configuration.input_source_options.result_values')}
                  </Select.Item>
                </Select>
              </InputGroup>
              <InputGroup title={t('renewals.tabs.configuration.input_field')}>
                <Input name="condition_input_field" register={register} />
              </InputGroup>
              <InputGroup title={t('renewals.tabs.configuration.operator')}>
                <Select
                  name="condition_operator"
                  register={register}
                  value={watch('condition_operator')}
                  useDefault>
                  <Select.Item value="exists">
                    {t('renewals.tabs.configuration.operator_options.exists')}
                  </Select.Item>
                  <Select.Item value="not_exists">
                    {t('renewals.tabs.configuration.operator_options.not_exists')}
                  </Select.Item>
                  <Select.Item value="is">
                    {t('renewals.tabs.configuration.operator_options.is')}
                  </Select.Item>
                  <Select.Item value="is_not">
                    {t('renewals.tabs.configuration.operator_options.is_not')}
                  </Select.Item>
                  <Select.Item value="in">
                    {t('renewals.tabs.configuration.operator_options.in')}
                  </Select.Item>
                  <Select.Item value="not_in">
                    {t('renewals.tabs.configuration.operator_options.not_in')}
                  </Select.Item>
                  <Select.Item value="contains">
                    {t('renewals.tabs.configuration.operator_options.contains')}
                  </Select.Item>
                  <Select.Item value="not_contains">
                    {t('renewals.tabs.configuration.operator_options.not_contains')}
                  </Select.Item>
                  <Select.Item value="not_empty">
                    {t('renewals.tabs.configuration.operator_options.not_empty')}
                  </Select.Item>
                  <Select.Item value="is_empty">
                    {t('renewals.tabs.configuration.operator_options.is_empty')}
                  </Select.Item>
                  <Select.Item value="less_than">
                    {t('renewals.tabs.configuration.operator_options.less_than')}
                  </Select.Item>
                  <Select.Item value="greater_than">
                    {t('renewals.tabs.configuration.operator_options.greater_than')}
                  </Select.Item>
                </Select>
              </InputGroup>
              {!['exists', 'not_exists', 'is_empty', 'not_empty'].includes(
                watch('condition_operator'),
              ) && (
                <InputGroup title="Value">
                  <Input name="condition_value" register={register} />
                </InputGroup>
              )}
            </>
          )}

          <InputGroup title="Frequency (days)">
            <Input name="frequency" register={register} type="number" />
          </InputGroup>
        </div>
      ) : (
        <>
          <InputGroup
            title="Days before expiration"
            tooltip="How many days before the expiration date should the renewal be triggered?">
            <Input name="days_before_expiration" register={register} type="number" />
          </InputGroup>
        </>
      )}

      <div className="d-flex justify-content-end u-margin-top--large">
        <Button type="secondary" onClick={onClose} className="u-margin-right">
          Cancel
        </Button>
        <Button type="primary" submit>
          Save Renewal
        </Button>
      </div>
    </form>
  );
};
